import { ApplicationRef, Injectable, PendingTasks } from '@angular/core';
import { DynamicComponentDirective } from '@phx/core-exports';
import { ComponentContext } from '../content/component-context';
import { ContentItem } from '../content/content-item';
import { LoggerService } from '../logging/logger.service';
import { ServerVarsService } from '../server-vars';
import { PhxPendingHttpTasks } from '../service/phx-pending-http-tasks.service';
import { isContentItem } from '../utils/is-content-item';
import { ContentItemTypeMapService } from './content-item-type-map.service';
import { RestrictionCheckService } from './restriction-check.service';

@Injectable({
    providedIn: 'root'
})
export class DynamicComponentService {
    constructor(
        private contentItemTypeMapService: ContentItemTypeMapService,
        private restrictionCheck: RestrictionCheckService,
        private logger: LoggerService,
        private applicationRef: ApplicationRef,
        private serverVars: ServerVarsService,
        private pendingHttpTasks: PhxPendingHttpTasks,
        private pendingTasks: PendingTasks
    ) {}

    clearComponents(placeholderElement: DynamicComponentDirective) {
        this.checkPlaceholderElement(placeholderElement);

        // clear the placeholder of old components first
        placeholderElement.viewContainerRef.clear();
    }

    async renderComponent(
        contentItem: ContentItem,
        placeholderElement: DynamicComponentDirective,
        componentContext?: ComponentContext,
        parent: ContentItem = null,
        skipContainer = false
    ) {
        if (!contentItem) {
            this.logger.warn(
                `[DYNAMIC COMPONENT] Given ContentItem is null (maybe not published in CMS)` +
                    (parent ? ` (Parent: ${parent.metadata?.id}, "${parent.metadata?.name}")` : '')
            );
            return;
        }

        if (!isContentItem(contentItem)) {
            this.logger.error(new Error(`[DYNAMIC COMPONENT] Not a valid ContentItem`));
            return;
        }

        // on each component there will be an array of all its parents in the data.parents property
        // unless the chain is broken, when the direct rendering parent is not passed on to renderComponent()
        if (parent) {
            // stripping `fields` as we don't need them here and they are causing issues (TypeError Converting circular structure to JSON) in TransferState for SSR
            const parents = [...(parent.data?.parents || []), { ...parent, fields: {} }] as ContentItem[];
            contentItem.data = { ...contentItem.data, parents };
        }

        this.checkPlaceholderElement(placeholderElement);

        // do we need to deal with restrictions and wrap the component in a container
        const nextContainer = this.getRestrictionContainer(contentItem);
        const useContainer = !!nextContainer && !skipContainer;

        // get component type to render from the ContentItem
        const componentTypeString = this.getComponentType(contentItem);

        // Find the componentClass of the desired ContentItem type
        const componentTypeStringToUse = useContainer ? nextContainer : componentTypeString;

        const componentClassInfo = this.getComponentClassInfoWithFallback(
            componentTypeStringToUse,
            contentItem.metadata.id
        );

        if (!componentClassInfo) {
            return;
        }

        let componentClass = null;

        if (componentClassInfo?.loader) {
            // handle lazily loaded component

            let componentClassEsModule: any = null;
            if (!componentClassInfo.isLoaded) {
                if (!componentContext.lazyLoadingComponentTypes) {
                    componentContext.lazyLoadingComponentTypes = new Set<string>();
                }
                componentContext.lazyLoadingComponentTypes.add(componentTypeString);
                const taskId = this.pendingHttpTasks.add();
                const removePendingTask = this.pendingTasks.add();

                try {
                    componentClassEsModule = await componentClassInfo.loader();
                    componentClassInfo.isLoaded = true;
                    componentClassInfo.loadedEsModule = componentClassEsModule;
                    componentContext.lazyLoadingComponentTypes.delete(componentTypeString);
                    this.pendingHttpTasks.remove(taskId);
                    removePendingTask();
                } catch (e) {
                    this.logger.warn(`Error lazy-loading component ${componentTypeString}: ${e}`);
                    componentContext.lazyLoadingComponentTypes.delete(componentTypeString);
                    this.pendingHttpTasks.remove(taskId);
                    removePendingTask();
                    return;
                }
            } else {
                componentClassEsModule = componentClassInfo.loadedEsModule;
            }

            componentClass = componentClassInfo.exportName
                ? componentClassEsModule[componentClassInfo.exportName]
                : componentClassEsModule['default'];
        } else {
            // handle preloaded/embedded component
            componentClass = componentClassInfo.component;
        }

        if (!componentClass) {
            this.logger.warn(
                `[DYNAMIC COMPONENT] Can't find export '${
                    componentClassInfo.exportName || 'default'
                }' in ES module for component '${componentTypeString}'.`
            );

            return;
        }

        // Create the component, attach it to the viewContainer and bind the data
        const applicationEnvironmentInjector = this.applicationRef.injector;
        const componentRef = placeholderElement.viewContainerRef.createComponent(componentClass, {
            environmentInjector: applicationEnvironmentInjector
        });

        // add the component's own contentItem to it
        componentRef.instance['contentItem'] = contentItem;

        // add additional data if available
        componentRef.instance['componentContext'] = componentContext;

        componentRef.changeDetectorRef.markForCheck();
    }

    renderComponentList(
        contentItems: ContentItem[],
        placeholderElement: DynamicComponentDirective,
        componentContext?: ComponentContext,
        parent?: ContentItem,
        stopAfterFirstValid = false,
        ignoreMissingComponents = false
    ) {
        if (!contentItems || contentItems.length === 0) {
            return;
        }

        if (!componentContext) {
            componentContext = {};
        }

        if (!isContentItem(contentItems[0])) {
            this.logger.error(new Error(`[DYNAMIC COMPONENT] Not a list of valid ContentItems`));
            return;
        }

        this.checkPlaceholderElement(placeholderElement);
        this.clearComponents(placeholderElement);

        // check if any component is in a lazy-loaded module and not yet loaded
        // to assure the correct rendering order of components in the given list
        if (!ignoreMissingComponents) {
            const loadModulePromises = [];
            for (const contentItem of contentItems) {
                const componentTypeString = this.getComponentType(contentItem);
                // check if component is lazy-loaded and if it needs to be loaded (not in map)
                const componentClassInfo = this.getComponentClassInfoWithFallback(
                    componentTypeString,
                    contentItem.metadata.id
                );

                if (componentClassInfo && componentClassInfo.loader && !componentClassInfo.isLoaded) {
                    // component is lazy-loaded and not yet available, so load it and postpone processing
                    if (!componentContext.lazyLoadingComponentTypes) {
                        componentContext.lazyLoadingComponentTypes = new Set<string>();
                    }
                    componentContext.lazyLoadingComponentTypes.add(componentTypeString);
                    const taskId = this.pendingHttpTasks.add();
                    const removePendingTask = this.pendingTasks.add();

                    loadModulePromises.push(
                        componentClassInfo
                            .loader()
                            .then(esModule => {
                                componentClassInfo.isLoaded = true;
                                componentClassInfo.loadedEsModule = esModule;
                            })
                            .catch(e => {
                                this.logger.warn(`Error lazy-loading component ${componentTypeString}: ${e}`);
                            })
                            .finally(() => {
                                componentContext.lazyLoadingComponentTypes.delete(componentTypeString);
                                this.pendingHttpTasks.remove(taskId);
                                removePendingTask();
                            })
                    );
                }
            }

            if (loadModulePromises.length > 0) {
                // wait for all promises and ignore errors
                Promise.all(loadModulePromises).then(() => {
                    // all loaded. try this thing again
                    this.renderComponentList(
                        contentItems,
                        placeholderElement,
                        componentContext,
                        parent,
                        stopAfterFirstValid,
                        true
                    );
                });
                // postpone processing until all needed modules are loaded
                return;
            }
        }

        // all should be available, so render them
        for (const contentItem of contentItems) {
            const checkResult = this.restrictionCheck.check(contentItem);
            if (checkResult.isValid) {
                this.renderComponent(contentItem, placeholderElement, componentContext, parent);

                if (stopAfterFirstValid && !checkResult.isDynamicallyEvaluated) {
                    return;
                }
            }
        }
    }

    private checkPlaceholderElement(placeholderElement: DynamicComponentDirective) {
        if (!placeholderElement && this.serverVars.getEnvironment() === 'local') {
            const msg = `'placeholderElement' is null == CMS content has no element to be attached to. Make sure you have:
            1) imported SharedModule to module where your CMS component is declared
            2) you have defined <ng-template #layoutPlaceholder phxDynamicComponent></ng-template> in your CMS component's template
            3) you have such property in your CMS component:
                @ViewChild('layoutPlaceholder', { read: DynamicComponentDirective })
                layoutPlaceholder: DynamicComponentDirective;
            `;
            throw new Error(msg);
        }
    }

    private getRestrictionContainer(contentItem: ContentItem) {
        const { restrictions } = contentItem.metadata;
        const anyRestrictions = restrictions && Object.values(restrictions).some(v => v);

        return anyRestrictions && 'DynamicComponentRestrictionContainerComponent';
    }

    private getComponentType(contentItem: ContentItem) {
        // if it is a feature component, get the actual component configured to render
        if (contentItem.metadata.type === 'featureComponent') {
            return contentItem.fields['componentName'] as string;
        }

        return contentItem.metadata.type;
    }

    private getComponentClassInfoWithFallback(componentTypeString: string, cmsId: string) {
        let componentClassInfo = this.contentItemTypeMapService.getMap().get(componentTypeString);
        if (!componentClassInfo) {
            this.logger.warn(
                `[DYNAMIC COMPONENT] Can't find client component for CMS type '${componentTypeString}' (id: '${cmsId}').`
            );

            if (this.serverVars.getEnvironment() === 'prod') {
                return;
            }
            // fallback for non-prod build:
            componentClassInfo = this.contentItemTypeMapService.getMap().get('__fallback__');
        }

        return componentClassInfo;
    }
}
